import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import {
  LogOut, Mail, FileText, MessageCircle, Star, Trash2, Eye, Plus, X,
  Key, Globe, Calendar, Edit3, Send, Clock, Archive, AlertCircle, Copy, Check
} from "lucide-react";

const STATUS_CONFIG = {
  draft: { label: "BOZZA", color: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30" },
  review: { label: "REVISIONE", color: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
  scheduled: { label: "PROGRAMMATO", color: "bg-purple-500/20 text-purple-400 border-purple-500/30" },
  published: { label: "PUBBLICATO", color: "bg-green-500/20 text-green-400 border-green-500/30" },
  archived: { label: "ARCHIVIATO", color: "bg-gray-500/20 text-gray-400 border-gray-500/30" },
};

function Tab({ active, onClick, icon: Icon, label, count }) {
  return (
    <button
      data-testid={`tab-${label.toLowerCase().replace(/\s/g, '-')}`}
      onClick={onClick}
      className={`flex items-center gap-2 px-5 py-3 font-mono text-sm tracking-wider transition-colors ${
        active ? "bg-[#FF3366] text-white" : "text-[#A3A3A3] hover:text-white border border-white/10"
      }`}
    >
      <Icon size={16} /> {label} {count > 0 && <span className="bg-white/20 px-2 py-0.5 text-xs">{count}</span>}
    </button>
  );
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono border ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function Field({ form, setForm, name, label, type = "text", required = false, maxLen, hint }) {
  return (
    <div>
      <label className="block text-xs font-mono tracking-widest text-[#A3A3A3] mb-1">
        {label} {maxLen && <span className="text-[#555]">({form[name]?.length || 0}/{maxLen})</span>}
      </label>
      {type === "textarea" ? (
        <textarea
          data-testid={`editor-${name}`}
          value={form[name] || ""}
          onChange={e => setForm(f => ({ ...f, [name]: e.target.value }))}
          className="w-full bg-transparent border border-white/20 p-3 text-white focus:outline-none focus:border-[#FF3366] min-h-[120px] text-sm"
          required={required}
          maxLength={maxLen}
        />
      ) : (
        <input
          data-testid={`editor-${name}`}
          type={type}
          value={form[name] || ""}
          onChange={e => setForm(f => ({ ...f, [name]: e.target.value }))}
          className="w-full bg-transparent border-b border-white/20 py-2 text-white focus:outline-none focus:border-[#FF3366] text-sm"
          required={required}
          maxLength={maxLen}
        />
      )}
      {hint && <p className="text-[#555] text-xs mt-1 font-mono">{hint}</p>}
    </div>
  );
}

function ArticleEditor({ article, onSave, onClose }) {
  const [form, setForm] = useState({
    title: "", excerpt: "", content: "", category: "", image_url: "",
    status: "draft", scheduled_at: "", seo_title: "", seo_description: "",
    seo_keywords: "", og_image: "", author: "Team NINFA",
    ...(article || {})
  });

  useEffect(() => {
    if (article) {
      setForm({
        ...article,
        seo_keywords: Array.isArray(article.seo_keywords) ? article.seo_keywords.join(", ") : (article.seo_keywords || ""),
        scheduled_at: article.scheduled_at ? article.scheduled_at.slice(0, 16) : "",
      });
    }
  }, [article]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      seo_keywords: form.seo_keywords ? form.seo_keywords.split(",").map(k => k.trim()).filter(Boolean) : [],
    };
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-[#0a0a0a] border border-white/10 w-full max-w-4xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <h3 className="font-heading text-xl font-bold">
            {article ? "MODIFICA ARTICOLO" : "NUOVO ARTICOLO"}
          </h3>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <h4 className="font-mono text-xs tracking-[0.2em] text-[#FF3366] mb-2">CONTENUTO</h4>
              <Field form={form} setForm={setForm} name="title" label="TITOLO" required />
              <Field form={form} setForm={setForm} name="excerpt" label="ESTRATTO" type="textarea" />
              <Field form={form} setForm={setForm} name="content" label="CONTENUTO" type="textarea" required />
              <div className="grid grid-cols-2 gap-4">
                <Field form={form} setForm={setForm} name="category" label="CATEGORIA" />
                <Field form={form} setForm={setForm} name="author" label="AUTORE" />
              </div>
              <Field form={form} setForm={setForm} name="image_url" label="URL IMMAGINE" hint="URL dell'immagine principale" />
            </div>

            <div className="space-y-4">
              <h4 className="font-mono text-xs tracking-[0.2em] text-[#FF3366] mb-2">PUBBLICAZIONE</h4>
              <div>
                <label className="block text-xs font-mono tracking-widest text-[#A3A3A3] mb-1">STATO</label>
                <select
                  data-testid="editor-status"
                  value={form.status}
                  onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                  className="w-full bg-[#121212] border border-white/20 p-2 text-white text-sm focus:outline-none focus:border-[#FF3366]"
                >
                  <option value="draft">Bozza</option>
                  <option value="review">In Revisione</option>
                  <option value="scheduled">Programmato</option>
                  <option value="published">Pubblicato</option>
                  <option value="archived">Archiviato</option>
                </select>
              </div>

              {form.status === "scheduled" && (
                <div>
                  <label className="block text-xs font-mono tracking-widest text-[#A3A3A3] mb-1">
                    DATA PUBBLICAZIONE (Europe/Rome)
                  </label>
                  <input
                    data-testid="editor-scheduled-at"
                    type="datetime-local"
                    value={form.scheduled_at || ""}
                    onChange={e => setForm(f => ({ ...f, scheduled_at: e.target.value }))}
                    className="w-full bg-[#121212] border border-white/20 p-2 text-white text-sm focus:outline-none focus:border-[#FF3366]"
                  />
                </div>
              )}

              <hr className="border-white/10 my-4" />
              <h4 className="font-mono text-xs tracking-[0.2em] text-[#FF3366] mb-2">SEO</h4>
              <Field form={form} setForm={setForm} name="seo_title" label="TITOLO SEO" maxLen={60} />
              <Field form={form} setForm={setForm} name="seo_description" label="META DESCRIPTION" type="textarea" maxLen={160} />
              <Field form={form} setForm={setForm} name="seo_keywords" label="KEYWORDS" hint="Separate da virgola" />
              <Field form={form} setForm={setForm} name="og_image" label="OG IMAGE URL" />
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-white/10">
            <button
              data-testid="editor-save-btn"
              type="submit"
              className="bg-[#FF3366] text-white font-bold uppercase tracking-widest px-8 py-3 hover:bg-white hover:text-black transition-colors text-sm"
            >
              {article ? "AGGIORNA" : "CREA ARTICOLO"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="border border-white/20 text-[#A3A3A3] font-bold uppercase tracking-widest px-6 py-3 hover:text-white hover:border-white transition-colors text-sm"
            >
              ANNULLA
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function APIKeyManager() {
  const [keys, setKeys] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyLimit, setNewKeyLimit] = useState(60);
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => { loadKeys(); }, []);

  const loadKeys = async () => {
    try {
      const res = await api.get("/admin/api-keys");
      setKeys(res.data);
    } catch (err) { console.error(err); }
  };

  const createKey = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post("/admin/api-keys", {
        name: newKeyName, permissions: ["create", "read", "update", "publish"], rate_limit: newKeyLimit
      });
      setCreatedKey(res.data);
      setShowCreate(false);
      setNewKeyName("");
      loadKeys();
    } catch (err) { console.error(err); }
  };

  const revokeKey = async (id) => {
    if (!window.confirm("Revocare questa API Key?")) return;
    try {
      await api.delete(`/admin/api-keys/${id}`);
      loadKeys();
    } catch (err) {
      alert(err.response?.data?.detail || "Errore nella revoca della chiave");
    }
  };

  const copyKey = () => {
    navigator.clipboard.writeText(createdKey.key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div data-testid="api-keys-panel">
      {createdKey && (
        <div className="mb-6 border border-green-500/30 bg-green-500/10 p-5">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-green-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-green-400 font-mono text-sm font-bold mb-2">API KEY CREATA</p>
              <p className="text-white/70 text-xs mb-3">Copia questa chiave ora. Non sara piu visibile.</p>
              <div className="flex items-center gap-2 bg-black/40 p-3 border border-white/10">
                <code data-testid="created-api-key-value" className="text-[#FF3366] text-sm flex-1 break-all">{createdKey.key}</code>
                <button data-testid="copy-api-key-btn" onClick={copyKey} className="text-white/60 hover:text-white flex-shrink-0">
                  {copied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                </button>
              </div>
            </div>
          </div>
          <button onClick={() => setCreatedKey(null)} className="mt-3 text-[#A3A3A3] text-xs font-mono hover:text-white">
            CHIUDI
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <button
          data-testid="create-api-key-btn"
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 bg-[#FF3366] text-white font-bold uppercase tracking-widest px-6 py-3 hover:bg-white hover:text-black transition-colors text-sm"
        >
          <Plus size={16} /> NUOVA API KEY
        </button>
      </div>

      {showCreate && (
        <form onSubmit={createKey} className="mb-6 border border-white/10 bg-[#121212] p-5 space-y-4">
          <div>
            <label className="block text-xs font-mono tracking-widest text-[#A3A3A3] mb-1">NOME AGENTE</label>
            <input
              data-testid="api-key-name-input"
              value={newKeyName}
              onChange={e => setNewKeyName(e.target.value)}
              className="w-full bg-transparent border-b border-white/20 py-2 text-white focus:outline-none focus:border-[#FF3366]"
              placeholder="es. Polsia"
              required
            />
          </div>
          <div>
            <label className="block text-xs font-mono tracking-widest text-[#A3A3A3] mb-1">RATE LIMIT (req/min)</label>
            <input
              data-testid="api-key-rate-input"
              type="number"
              value={newKeyLimit}
              onChange={e => setNewKeyLimit(parseInt(e.target.value) || 60)}
              className="w-full bg-transparent border-b border-white/20 py-2 text-white focus:outline-none focus:border-[#FF3366]"
            />
          </div>
          <div className="flex gap-3">
            <button data-testid="api-key-generate-btn" type="submit" className="bg-[#FF3366] text-white font-bold text-sm px-6 py-2">GENERA</button>
            <button type="button" onClick={() => setShowCreate(false)} className="text-[#A3A3A3] text-sm">ANNULLA</button>
          </div>
        </form>
      )}

      <div className="space-y-3">
        {keys.length === 0 && <p className="text-[#A3A3A3] font-mono text-sm">Nessuna API Key creata.</p>}
        {keys.map(k => (
          <div key={k.id} className={`border p-5 flex items-center justify-between ${k.active ? "border-white/10 bg-[#121212]" : "border-red-500/20 bg-red-500/5 opacity-60"}`}>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <span className="font-heading font-bold">{k.name}</span>
                <code className="text-[#FF3366] text-xs font-mono">{k.key_prefix}...</code>
                {!k.active && <span className="text-red-400 text-xs font-mono">REVOCATA</span>}
              </div>
              <p className="text-[#A3A3A3] text-xs font-mono">
                Rate: {k.rate_limit} req/min | Usi: {k.usage_count || 0}
                {k.last_used_at && ` | Ultimo: ${new Date(k.last_used_at).toLocaleString("it-IT")}`}
              </p>
            </div>
            {k.active && (
              <button
                data-testid={`revoke-key-${k.id}`}
                onClick={() => revokeKey(k.id)}
                className="p-2 text-[#A3A3A3] hover:text-red-400 transition-colors"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function WebhookManager() {
  const [webhooks, setWebhooks] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newUrl, setNewUrl] = useState("");

  useEffect(() => { loadWebhooks(); }, []);

  const loadWebhooks = async () => {
    try {
      const res = await api.get("/admin/webhooks");
      setWebhooks(res.data);
    } catch (err) { console.error(err); }
  };

  const createWebhook = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/webhooks", { url: newUrl, events: ["article.published"] });
      setShowCreate(false);
      setNewUrl("");
      loadWebhooks();
    } catch (err) {
      alert(err.response?.data?.detail || "Errore nella creazione del webhook");
    }
  };

  const deleteWebhook = async (id) => {
    try {
      await api.delete(`/admin/webhooks/${id}`);
      loadWebhooks();
    } catch (err) {
      alert(err.response?.data?.detail || "Errore nell'eliminazione del webhook");
    }
  };

  return (
    <div data-testid="webhooks-panel" className="mt-8">
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-mono text-xs tracking-[0.2em] text-[#FF3366]">WEBHOOKS</h4>
        <button
          data-testid="create-webhook-btn"
          onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 text-[#A3A3A3] hover:text-white font-mono text-xs"
        >
          <Plus size={14} /> AGGIUNGI
        </button>
      </div>

      {showCreate && (
        <form onSubmit={createWebhook} className="mb-4 flex gap-3">
          <input
            data-testid="webhook-url-input"
            value={newUrl}
            onChange={e => setNewUrl(e.target.value)}
            placeholder="https://..."
            className="flex-1 bg-transparent border-b border-white/20 py-2 text-white text-sm focus:outline-none focus:border-[#FF3366]"
            required
          />
          <button data-testid="webhook-save-btn" type="submit" className="bg-[#FF3366] text-white text-xs px-4 py-2">SALVA</button>
          <button type="button" onClick={() => setShowCreate(false)} className="text-[#A3A3A3] text-xs">X</button>
        </form>
      )}

      {webhooks.length === 0 && <p className="text-[#555] font-mono text-xs">Nessun webhook configurato.</p>}
      {webhooks.map(w => (
        <div key={w.id} className="border border-white/10 bg-[#121212] p-3 mb-2 flex items-center justify-between text-sm">
          <div>
            <code className="text-white/80 text-xs">{w.url}</code>
            <p className="text-[#555] text-xs font-mono mt-1">
              Eventi: {w.events?.join(", ")} | Errori: {w.failure_count || 0}
            </p>
          </div>
          <button data-testid={`delete-webhook-${w.id}`} onClick={() => deleteWebhook(w.id)} className="text-[#A3A3A3] hover:text-red-400"><Trash2 size={14} /></button>
        </div>
      ))}
    </div>
  );
}

function PublishingCalendar({ articles }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDayOfWeek = new Date(year, month, 1).getDay();
  const adjustedFirst = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

  const scheduled = articles.filter(a => a.status === "scheduled" && a.scheduled_at);
  const published = articles.filter(a => a.status === "published" && a.published_at);

  const getArticlesForDay = (day) => {
    const dayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const sched = scheduled.filter(a => a.scheduled_at?.startsWith(dayStr));
    const pub = published.filter(a => a.published_at?.startsWith(dayStr));
    return { scheduled: sched, published: pub };
  };

  const prevMonth = () => setCurrentMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentMonth(new Date(year, month + 1, 1));

  const days = ["LUN", "MAR", "MER", "GIO", "VEN", "SAB", "DOM"];
  const monthNames = ["GENNAIO", "FEBBRAIO", "MARZO", "APRILE", "MAGGIO", "GIUGNO",
    "LUGLIO", "AGOSTO", "SETTEMBRE", "OTTOBRE", "NOVEMBRE", "DICEMBRE"];

  return (
    <div data-testid="publishing-calendar" className="border border-white/10 bg-[#0a0a0a] p-4 mb-6">
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="text-[#A3A3A3] hover:text-white font-mono text-sm px-3 py-1">&lt;</button>
        <span className="font-mono text-sm tracking-[0.2em] text-white">
          {monthNames[month]} {year}
        </span>
        <button onClick={nextMonth} className="text-[#A3A3A3] hover:text-white font-mono text-sm px-3 py-1">&gt;</button>
      </div>

      <div className="grid grid-cols-7 gap-1 text-center">
        {days.map(d => (
          <div key={d} className="text-[#555] font-mono text-xs py-1">{d}</div>
        ))}
        {Array.from({ length: adjustedFirst }).map((_, i) => (
          <div key={`empty-${i}`} />
        ))}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day = i + 1;
          const { scheduled: sched, published: pub } = getArticlesForDay(day);
          const hasItems = sched.length > 0 || pub.length > 0;
          const today = new Date();
          const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

          return (
            <div
              key={day}
              className={`relative p-1.5 min-h-[40px] text-xs font-mono border transition-colors ${
                isToday ? "border-[#FF3366]/50 bg-[#FF3366]/5" : "border-transparent"
              } ${hasItems ? "bg-white/5" : ""}`}
              title={[...sched.map(a => `Prog: ${a.title}`), ...pub.map(a => `Pub: ${a.title}`)].join('\n')}
            >
              <span className={isToday ? "text-[#FF3366]" : "text-[#A3A3A3]"}>{day}</span>
              {sched.length > 0 && <div className="w-1.5 h-1.5 bg-purple-500 rounded-full absolute bottom-1 left-1/2 -translate-x-2" />}
              {pub.length > 0 && <div className="w-1.5 h-1.5 bg-green-500 rounded-full absolute bottom-1 left-1/2 translate-x-0.5" />}
            </div>
          );
        })}
      </div>

      <div className="flex gap-4 mt-3 text-xs font-mono text-[#A3A3A3]">
        <span className="flex items-center gap-1"><div className="w-2 h-2 bg-purple-500 rounded-full" /> Programmati</span>
        <span className="flex items-center gap-1"><div className="w-2 h-2 bg-green-500 rounded-full" /> Pubblicati</span>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("editorial");
  const [contacts, setContacts] = useState([]);
  const [articles, setArticles] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [chats, setChats] = useState([]);
  const [apiKeyCount, setApiKeyCount] = useState(0);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [showLegacyModal, setShowLegacyModal] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [c, a, t, ch, k] = await Promise.all([
        api.get("/admin/contacts"),
        api.get("/admin/articles"),
        api.get("/testimonials"),
        api.get("/admin/chats"),
        api.get("/admin/api-keys")
      ]);
      setContacts(c.data);
      setArticles(a.data);
      setTestimonials(t.data);
      setChats(ch.data);
      setApiKeyCount(k.data.filter(key => key.active).length);
    } catch (err) {
      console.error("Error loading data:", err);
    }
  };

  const handleLogout = async () => { await logout(); navigate("/admin"); };

  const markRead = async (id) => {
    await api.put(`/admin/contacts/${id}/read`);
    setContacts(cs => cs.map(c => c.id === id ? { ...c, read: true } : c));
  };

  const deleteItem = async (type, id) => {
    if (type === "articles") {
      await api.delete(`/admin/articles/${id}`);
    } else if (type === "contacts") {
      await api.delete(`/admin/contacts/${id}`);
    } else if (type === "testimonials") {
      await api.delete(`/admin/testimonials/${id}`);
    }
    loadData();
  };

  const saveArticle = async (data) => {
    try {
      if (editingArticle) {
        const changed = {};
        for (const key of Object.keys(data)) {
          if (["id", "slug", "revision_history", "created_at", "updated_at", "published_at", "created_by_agent"].includes(key)) continue;
          const oldVal = editingArticle[key];
          const newVal = data[key];
          if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
            changed[key] = newVal;
          }
        }
        if (Object.keys(changed).length > 0) {
          await api.put(`/admin/articles/${editingArticle.id}`, changed);
        }
      } else {
        await api.post("/admin/articles", data);
      }
      setShowEditor(false);
      setEditingArticle(null);
      loadData();
    } catch (err) {
      alert(err.response?.data?.detail || "Errore nel salvataggio");
    }
  };

  const addTestimonial = async (values) => {
    await api.post("/admin/testimonials", { ...values, rating: parseInt(values.rating) || 5 });
    setShowLegacyModal(null);
    loadData();
  };

  const unreadCount = contacts.filter(c => !c.read).length;
  const filteredArticles = statusFilter
    ? articles.filter(a => a.status === statusFilter)
    : articles;

  const statusCounts = articles.reduce((acc, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050505]">
      <header className="border-b border-white/10 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-heading text-xl font-black tracking-tighter">NINFA</span>
            <span className="text-[#A3A3A3] font-mono text-xs tracking-wider">CMS EDITORIALE</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-[#A3A3A3] font-mono text-sm">{user?.email}</span>
            <button data-testid="admin-logout-btn" onClick={handleLogout} className="flex items-center gap-2 text-[#FF3366] hover:text-white transition-colors font-mono text-sm">
              <LogOut size={16} /> ESCI
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-wrap gap-3 mb-8">
          <Tab active={tab === "editorial"} onClick={() => setTab("editorial")} icon={FileText} label="EDITORIALE" count={articles.length} />
          <Tab active={tab === "contacts"} onClick={() => setTab("contacts")} icon={Mail} label="MESSAGGI" count={unreadCount} />
          <Tab active={tab === "testimonials"} onClick={() => setTab("testimonials")} icon={Star} label="TESTIMONIANZE" count={testimonials.length} />
          <Tab active={tab === "chats"} onClick={() => setTab("chats")} icon={MessageCircle} label="CHAT" count={chats.length} />
          <Tab active={tab === "apikeys"} onClick={() => setTab("apikeys")} icon={Key} label="API KEYS" count={apiKeyCount} />
        </div>

        {/* ── EDITORIAL TAB ── */}
        {tab === "editorial" && (
          <div>
            <PublishingCalendar articles={articles} />

            <div className="flex flex-wrap items-center gap-3 mb-6">
              <button
                data-testid="new-article-btn"
                onClick={() => { setEditingArticle(null); setShowEditor(true); }}
                className="flex items-center gap-2 bg-[#FF3366] text-white font-bold uppercase tracking-widest px-6 py-3 hover:bg-white hover:text-black transition-colors text-sm"
              >
                <Plus size={16} /> NUOVO ARTICOLO
              </button>

              <div className="flex gap-2 ml-auto">
                <button
                  data-testid="status-filter-all"
                  onClick={() => setStatusFilter("")}
                  className={`px-3 py-1.5 text-xs font-mono border transition-colors ${
                    !statusFilter ? "border-[#FF3366] text-[#FF3366]" : "border-white/10 text-[#A3A3A3] hover:text-white"
                  }`}
                >
                  TUTTI ({articles.length})
                </button>
                {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                  <button
                    key={key}
                    data-testid={`status-filter-${key}`}
                    onClick={() => setStatusFilter(statusFilter === key ? "" : key)}
                    className={`px-3 py-1.5 text-xs font-mono border transition-colors ${
                      statusFilter === key ? `${cfg.color}` : "border-white/10 text-[#A3A3A3] hover:text-white"
                    }`}
                  >
                    {cfg.label} ({statusCounts[key] || 0})
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              {filteredArticles.length === 0 && (
                <p className="text-[#A3A3A3] font-mono">Nessun articolo trovato.</p>
              )}
              {filteredArticles.map(a => (
                <div key={a.id} className="border border-white/10 bg-[#121212] p-5 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <StatusBadge status={a.status} />
                      <span className="text-xs font-mono text-[#FF3366] tracking-wider">{a.category?.toUpperCase()}</span>
                      {a.created_by_agent && (
                        <span className="text-xs font-mono text-blue-400 border border-blue-500/30 px-1.5 py-0.5">
                          AI: {a.created_by_agent}
                        </span>
                      )}
                    </div>
                    <h3 className="font-heading font-bold text-white">{a.title}</h3>
                    <p className="text-[#A3A3A3] text-sm mt-1 line-clamp-1">{a.excerpt}</p>
                    <div className="flex gap-4 mt-2 text-xs font-mono text-[#555]">
                      <span>{a.author}</span>
                      {a.slug && <span>/{a.slug}</span>}
                      {a.status === "scheduled" && a.scheduled_at && (
                        <span className="text-purple-400 flex items-center gap-1">
                          <Clock size={10} /> {new Date(a.scheduled_at).toLocaleString("it-IT")}
                        </span>
                      )}
                      {a.published_at && (
                        <span className="text-green-400/60">
                          Pubblicato: {new Date(a.published_at).toLocaleDateString("it-IT")}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      data-testid={`edit-article-${a.id}`}
                      onClick={() => { setEditingArticle(a); setShowEditor(true); }}
                      className="p-2 text-[#A3A3A3] hover:text-[#FF3366] transition-colors"
                      title="Modifica"
                    >
                      <Edit3 size={16} />
                    </button>
                    <button
                      data-testid={`delete-article-${a.id}`}
                      onClick={() => { if (window.confirm("Eliminare questo articolo?")) deleteItem("articles", a.id); }}
                      className="p-2 text-[#A3A3A3] hover:text-red-400 transition-colors"
                      title="Elimina"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── CONTACTS TAB ── */}
        {tab === "contacts" && (
          <div className="space-y-3">
            {contacts.length === 0 && <p className="text-[#A3A3A3] font-mono">Nessun messaggio ricevuto.</p>}
            {contacts.map(c => (
              <div key={c.id} data-testid={`contact-item-${c.id}`} className={`border p-5 flex items-start justify-between gap-4 ${c.read ? "border-white/10 bg-[#121212]" : "border-[#FF3366]/30 bg-[#FF3366]/5"}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="font-heading font-bold">{c.name}</span>
                    {!c.read && <span className="bg-[#FF3366] text-white text-xs px-2 py-0.5 font-mono">NUOVO</span>}
                  </div>
                  <p className="text-[#A3A3A3] text-sm font-mono">{c.email} {c.phone && `| ${c.phone}`} {c.company && `| ${c.company}`}</p>
                  <p className="text-white/80 mt-2 text-sm">{c.message}</p>
                  <p className="text-[#A3A3A3] text-xs font-mono mt-2">{new Date(c.created_at).toLocaleString("it-IT")}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {!c.read && <button onClick={() => markRead(c.id)} className="p-2 text-[#A3A3A3] hover:text-[#FF3366] transition-colors"><Eye size={16} /></button>}
                  <button onClick={() => deleteItem("contacts", c.id)} className="p-2 text-[#A3A3A3] hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── TESTIMONIALS TAB ── */}
        {tab === "testimonials" && (
          <div>
            <button data-testid="add-testimonial-btn" onClick={() => setShowLegacyModal("testimonial")} className="mb-6 flex items-center gap-2 bg-[#FF3366] text-white font-bold uppercase tracking-widest px-6 py-3 hover:bg-white hover:text-black transition-colors text-sm">
              <Plus size={16} /> NUOVA TESTIMONIANZA
            </button>
            <div className="space-y-3">
              {testimonials.map(t => (
                <div key={t.id} className="border border-white/10 bg-[#121212] p-5 flex items-start justify-between">
                  <div>
                    <p className="text-white/80 italic">"{t.quote}"</p>
                    <p className="text-[#FF3366] font-mono text-sm mt-2">{t.name} — {t.role}, {t.company}</p>
                  </div>
                  <button onClick={() => deleteItem("testimonials", t.id)} className="p-2 text-[#A3A3A3] hover:text-red-400 transition-colors flex-shrink-0"><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── CHATS TAB ── */}
        {tab === "chats" && (
          <div className="space-y-3">
            {chats.length === 0 && <p className="text-[#A3A3A3] font-mono">Nessun messaggio chat.</p>}
            {chats.map(c => (
              <div key={c.id} className="border border-white/10 bg-[#121212] p-5">
                <div className="flex items-center gap-3 mb-2">
                  <span className="font-bold">{c.name}</span>
                  <span className="text-[#A3A3A3] text-xs font-mono">{new Date(c.created_at).toLocaleString("it-IT")}</span>
                </div>
                <p className="text-white/80 text-sm">{c.message}</p>
              </div>
            ))}
          </div>
        )}

        {/* ── API KEYS TAB ── */}
        {tab === "apikeys" && (
          <div>
            <APIKeyManager />
            <WebhookManager />
          </div>
        )}
      </div>

      {/* ── ARTICLE EDITOR MODAL ── */}
      {showEditor && (
        <ArticleEditor
          article={editingArticle}
          onSave={saveArticle}
          onClose={() => { setShowEditor(false); setEditingArticle(null); }}
        />
      )}

      {/* ── LEGACY TESTIMONIAL MODAL ── */}
      {showLegacyModal === "testimonial" && (
        <LegacyModal
          title="Nuova Testimonianza"
          fields={[
            { name: "name", label: "NOME", required: true },
            { name: "company", label: "AZIENDA", required: true },
            { name: "role", label: "RUOLO", required: true },
            { name: "quote", label: "CITAZIONE", type: "textarea", required: true },
            { name: "rating", label: "VALUTAZIONE (1-5)", type: "number" },
          ]}
          onSubmit={addTestimonial}
          onClose={() => setShowLegacyModal(null)}
        />
      )}
    </div>
  );
}

function LegacyModal({ title, fields, onSubmit, onClose }) {
  const [values, setValues] = useState({});
  const handleSubmit = (e) => { e.preventDefault(); onSubmit(values); };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-6">
      <div className="bg-[#121212] border border-white/10 w-full max-w-lg p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-heading text-xl font-bold">{title}</h3>
          <button onClick={onClose} className="text-[#A3A3A3] hover:text-white"><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {fields.map(f => (
            <div key={f.name}>
              <label className="block text-xs font-mono tracking-widest text-[#A3A3A3] mb-1">{f.label}</label>
              {f.type === "textarea" ? (
                <textarea value={values[f.name] || ""} onChange={e => setValues(v => ({ ...v, [f.name]: e.target.value }))}
                  className="w-full bg-transparent border border-white/20 p-3 text-white focus:outline-none focus:border-[#FF3366] min-h-[100px]" required={f.required} />
              ) : (
                <input type={f.type || "text"} value={values[f.name] || ""} onChange={e => setValues(v => ({ ...v, [f.name]: e.target.value }))}
                  className="w-full bg-transparent border-b border-white/20 py-2 text-white focus:outline-none focus:border-[#FF3366]" required={f.required} />
              )}
            </div>
          ))}
          <button type="submit" className="bg-[#FF3366] text-white font-bold uppercase tracking-widest px-6 py-3 hover:bg-white hover:text-black transition-colors text-sm">AGGIUNGI</button>
        </form>
      </div>
    </div>
  );
}
